import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { ScrollToTop } from "./components/ScrollToTop";
import { PWAInstall } from "./components/PWAInstall";
import { PageLoader } from "./components/PageLoader";

// Eager load the index page for fast initial render
import Index from "./pages/Index";

// Lazy load all other pages for code splitting
const Styles = lazy(() => import("./pages/Styles"));
const StyleDetail = lazy(() => import("./pages/StyleDetail"));
const CustomStyleCreatorPage = lazy(() => import("./pages/CustomStyleCreator"));
const BatchTransformPage = lazy(() => import("./pages/BatchTransformPage"));
const UnicodeLookupPage = lazy(() => import("./pages/UnicodeLookupPage"));
const QRCodePage = lazy(() => import("./pages/QRCodePage"));
const LoremIpsumPage = lazy(() => import("./pages/LoremIpsumPage"));
const DuplicateRemoverPage = lazy(() => import("./pages/DuplicateRemoverPage"));
const SortLinesPage = lazy(() => import("./pages/SortLinesPage"));
const TextDiffPage = lazy(() => import("./pages/TextDiffPage"));
const FindReplacePage = lazy(() => import("./pages/FindReplacePage"));
const WordCounterPage = lazy(() => import("./pages/WordCounterPage"));
const CaseConverterPage = lazy(() => import("./pages/CaseConverterPage"));
const HashtagGeneratorPage = lazy(() => import("./pages/HashtagGeneratorPage"));
const InvisibleTextPage = lazy(() => import("./pages/InvisibleTextPage"));
const TextReverserPage = lazy(() => import("./pages/TextReverserPage"));
const TextRepeaterPage = lazy(() => import("./pages/TextRepeaterPage"));
const TextEncoderPage = lazy(() => import("./pages/TextEncoderPage"));
const AlphabeticalOrderPage = lazy(() => import("./pages/AlphabeticalOrderPage"));
const SocialPreviewPage = lazy(() => import("./pages/SocialPreviewPage"));
const EmbedWidgetPage = lazy(() => import("./pages/EmbedWidgetPage"));
const InstagramGenerator = lazy(() => import("./pages/InstagramGenerator"));
const DiscordGenerator = lazy(() => import("./pages/DiscordGenerator"));
const TikTokGenerator = lazy(() => import("./pages/TikTokGenerator"));
const FacebookGenerator = lazy(() => import("./pages/FacebookGenerator"));
const TwitterGenerator = lazy(() => import("./pages/TwitterGenerator"));
const YouTubeGenerator = lazy(() => import("./pages/YouTubeGenerator"));
const WhatsAppGenerator = lazy(() => import("./pages/WhatsAppGenerator"));
const PinterestGenerator = lazy(() => import("./pages/PinterestGenerator"));
const LinkedInGenerator = lazy(() => import("./pages/LinkedInGenerator"));
const Platforms = lazy(() => import("./pages/Platforms"));
const Tools = lazy(() => import("./pages/Tools"));
const About = lazy(() => import("./pages/About"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Contact = lazy(() => import("./pages/Contact"));
const InstallApp = lazy(() => import("./pages/InstallApp"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    // Add manifest link
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (!manifestLink) {
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = '/manifest.json';
      document.head.appendChild(link);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/styles" element={<Styles />} />
              <Route path="/style/:slug" element={<StyleDetail />} />
              <Route path="/custom-style" element={<CustomStyleCreatorPage />} />
              {/* Tool Pages */}
              <Route path="/tools" element={<Tools />} />
              <Route path="/tools/batch-transform" element={<BatchTransformPage />} />
              <Route path="/tools/unicode-lookup" element={<UnicodeLookupPage />} />
              <Route path="/tools/qr-code" element={<QRCodePage />} />
              <Route path="/tools/lorem-ipsum" element={<LoremIpsumPage />} />
              <Route path="/tools/duplicate-remover" element={<DuplicateRemoverPage />} />
              <Route path="/tools/sort-lines" element={<SortLinesPage />} />
              <Route path="/tools/text-diff" element={<TextDiffPage />} />
              <Route path="/tools/find-replace" element={<FindReplacePage />} />
              <Route path="/tools/word-counter" element={<WordCounterPage />} />
              <Route path="/tools/case-converter" element={<CaseConverterPage />} />
              <Route path="/tools/hashtag-generator" element={<HashtagGeneratorPage />} />
              <Route path="/tools/invisible-text" element={<InvisibleTextPage />} />
              <Route path="/tools/text-reverser" element={<TextReverserPage />} />
              <Route path="/tools/text-repeater" element={<TextRepeaterPage />} />
              <Route path="/tools/text-encoder" element={<TextEncoderPage />} />
              <Route path="/tools/alphabetical-order" element={<AlphabeticalOrderPage />} />
              <Route path="/tools/social-preview" element={<SocialPreviewPage />} />
              <Route path="/tools/embed" element={<EmbedWidgetPage />} />
              {/* SEO Landing Pages */}
              <Route path="/platforms" element={<Platforms />} />
              <Route path="/instagram-fonts" element={<InstagramGenerator />} />
              <Route path="/discord-fonts" element={<DiscordGenerator />} />
              <Route path="/tiktok-fonts" element={<TikTokGenerator />} />
              <Route path="/facebook-fonts" element={<FacebookGenerator />} />
              <Route path="/twitter-fonts" element={<TwitterGenerator />} />
              <Route path="/youtube-fonts" element={<YouTubeGenerator />} />
              <Route path="/whatsapp-fonts" element={<WhatsAppGenerator />} />
              <Route path="/pinterest-fonts" element={<PinterestGenerator />} />
              <Route path="/linkedin-fonts" element={<LinkedInGenerator />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/install" element={<InstallApp />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <PWAInstall />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
